import { useTheme } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { observer } from 'mobx-react-lite';

import { Currency } from '../../graphql/graphql';

const FONT_SRC =
  'https://fonts.googleapis.com/css?family=Roboto:300,400,500,900&display=swap';
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUB_KEY);

const StripeElementsProvider = observer(
  ({
    currency,
    children,
  }: {
    currency: Currency;
    children: React.ReactNode;
  }) => {
    const theme = useTheme();

    return (
      <Elements
        stripe={stripePromise}
        options={{
          mode: 'subscription',
          amount: 0,
          currency: currency.toLowerCase(),
          fonts: [{ cssSrc: FONT_SRC }],
          appearance: {
            theme: 'flat',
            variables: {
              colorPrimary: theme.palette.common.white,
              colorText: theme.palette.secondary.dark,
              colorTextSecondary: theme.palette.secondary.main,
              fontFamily: theme.typography.fontFamily,
              fontSizeBase: `${theme.typography.fontSize}px`,
              colorDanger: theme.palette.error.main,
              gridColumnSpacing: theme.spacing(1),
              gridRowSpacing: theme.spacing(1),
            },
            rules: {
              '.Input': {
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.secondary.light}`,
                borderRadius: '2px',
                padding: '2px 6px',
                lineHeight: '18px',
              },
              '.Input:focus': {
                boxShadow: 'none',
                outline: 'none',
                borderColor: theme.palette.primary.main,
              },
              '.Input--invalid': {
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.error.main}`,
                boxShadow: 'none',
                borderRadius: '2px',
                padding: '2px 6px',
                lineHeight: '18px',
              },
              '.Label': {
                fontWeight: '500',
                padding: '5px 0',
                margin: '0',
                fontSize: `${theme.typography.fontSize}px`,
              },
              '.Tab': {
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.grey[300]}`,
                fontWeight: 'normal',
                fontSize: `${theme.typography.fontSize}px`,
              },
              '.Tab--selected': {
                border: `1px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        {children}
      </Elements>
    );
  },
);

export default StripeElementsProvider;
